/**
 * Custom hook for managing multi-quote mileage simulator state
 * Supports multiple quote options per flight leg with automatic ranking
 */

import { useReducer, useCallback, useMemo } from 'react';
import type {
  Quote,
  Combo,
  MultiQuoteSimulatorState,
  ProfitAnalysis,
  ComboRanking,
} from '../types';
import { MileageProgram } from '../types';
import { generateComboRanking, validateMultiQuoteQuotation } from '../utils/combos';
import { calculateProfitAnalysisFromDesiredProfit } from '../utils/calculations';

type MultiQuoteAction =
  | { type: 'SET_AIRLINE_PRICE'; payload: number }
  | { type: 'SET_ROUND_TRIP'; payload: boolean }
  | { type: 'ADD_OUTBOUND_QUOTE'; payload: Quote }
  | { type: 'UPDATE_OUTBOUND_QUOTE'; payload: { id: string; updates: Partial<Quote> } }
  | { type: 'REMOVE_OUTBOUND_QUOTE'; payload: string }
  | { type: 'ADD_RETURN_QUOTE'; payload: Quote }
  | { type: 'UPDATE_RETURN_QUOTE'; payload: { id: string; updates: Partial<Quote> } }
  | { type: 'REMOVE_RETURN_QUOTE'; payload: string }
  | { type: 'SET_DESIRED_CHARGE'; payload: number }
  | { type: 'SELECT_COMBO'; payload: Combo | null }
  | { type: 'RESET' };

/**
 * Create a new empty quote
 */
function createEmptyQuote(id: string): Quote {
  return {
    id,
    program: MileageProgram.SMILES,
    miles: 0,
    costPerThousand: 0,
    boardingTax: 0,
  };
}

/**
 * Generate a unique ID for quotes
 */
function generateQuoteId(type: 'outbound' | 'return'): string {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

const initialState: MultiQuoteSimulatorState = {
  quotation: {
    airlinePrice: 0,
    outboundQuotes: [createEmptyQuote(generateQuoteId('outbound'))],
    returnQuotes: [],
    isRoundTrip: false,
  },
  desiredChargeAmount: 0,
  ranking: null,
  selectedCombo: null,
  profitAnalysis: null,
};

function multiQuoteReducer(
  state: MultiQuoteSimulatorState,
  action: MultiQuoteAction
): MultiQuoteSimulatorState {
  switch (action.type) {
    case 'SET_AIRLINE_PRICE':
      return {
        ...state,
        quotation: {
          ...state.quotation,
          airlinePrice: action.payload,
        },
      };

    case 'SET_ROUND_TRIP': {
      const isRoundTrip = action.payload;
      return {
        ...state,
        quotation: {
          ...state.quotation,
          isRoundTrip,
          // Initialize with one empty return quote if enabling round-trip
          returnQuotes: isRoundTrip && state.quotation.returnQuotes.length === 0
            ? [createEmptyQuote(generateQuoteId('return'))]
            : state.quotation.returnQuotes,
        },
      };
    }

    case 'ADD_OUTBOUND_QUOTE':
      return {
        ...state,
        quotation: {
          ...state.quotation,
          outboundQuotes: [...state.quotation.outboundQuotes, action.payload],
        },
      };

    case 'UPDATE_OUTBOUND_QUOTE': {
      const { id, updates } = action.payload;
      return {
        ...state,
        quotation: {
          ...state.quotation,
          outboundQuotes: state.quotation.outboundQuotes.map(quote =>
            quote.id === id ? { ...quote, ...updates } : quote
          ),
        },
      };
    }

    case 'REMOVE_OUTBOUND_QUOTE': {
      // Don't allow removing the last quote
      if (state.quotation.outboundQuotes.length <= 1) {
        return state;
      }
      return {
        ...state,
        quotation: {
          ...state.quotation,
          outboundQuotes: state.quotation.outboundQuotes.filter(
            quote => quote.id !== action.payload
          ),
        },
      };
    }

    case 'ADD_RETURN_QUOTE':
      return {
        ...state,
        quotation: {
          ...state.quotation,
          returnQuotes: [...state.quotation.returnQuotes, action.payload],
        },
      };

    case 'UPDATE_RETURN_QUOTE': {
      const { id, updates } = action.payload;
      return {
        ...state,
        quotation: {
          ...state.quotation,
          returnQuotes: state.quotation.returnQuotes.map(quote =>
            quote.id === id ? { ...quote, ...updates } : quote
          ),
        },
      };
    }

    case 'REMOVE_RETURN_QUOTE': {
      // Don't allow removing the last quote if round-trip
      if (state.quotation.isRoundTrip && state.quotation.returnQuotes.length <= 1) {
        return state;
      }
      return {
        ...state,
        quotation: {
          ...state.quotation,
          returnQuotes: state.quotation.returnQuotes.filter(
            quote => quote.id !== action.payload
          ),
        },
      };
    }

    case 'SET_DESIRED_CHARGE':
      return {
        ...state,
        desiredChargeAmount: action.payload,
      };

    case 'SELECT_COMBO':
      return {
        ...state,
        selectedCombo: action.payload,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function useMultiQuoteSimulator() {
  const [state, dispatch] = useReducer(multiQuoteReducer, initialState);

  // Actions - Airline Price
  const setAirlinePrice = useCallback((price: number) => {
    dispatch({ type: 'SET_AIRLINE_PRICE', payload: price });
  }, []);

  // Actions - Round Trip
  const setRoundTrip = useCallback((isRoundTrip: boolean) => {
    dispatch({ type: 'SET_ROUND_TRIP', payload: isRoundTrip });
  }, []);

  // Actions - Outbound Quotes
  const addOutboundQuote = useCallback(() => {
    const newQuote = createEmptyQuote(generateQuoteId('outbound'));
    dispatch({ type: 'ADD_OUTBOUND_QUOTE', payload: newQuote });
  }, []);

  const updateOutboundQuote = useCallback((id: string, updates: Partial<Quote>) => {
    dispatch({ type: 'UPDATE_OUTBOUND_QUOTE', payload: { id, updates } });
  }, []);

  const removeOutboundQuote = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_OUTBOUND_QUOTE', payload: id });
  }, []);

  // Actions - Return Quotes
  const addReturnQuote = useCallback(() => {
    const newQuote = createEmptyQuote(generateQuoteId('return'));
    dispatch({ type: 'ADD_RETURN_QUOTE', payload: newQuote });
  }, []);

  const updateReturnQuote = useCallback((id: string, updates: Partial<Quote>) => {
    dispatch({ type: 'UPDATE_RETURN_QUOTE', payload: { id, updates } });
  }, []);

  const removeReturnQuote = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_RETURN_QUOTE', payload: id });
  }, []);

  // Actions - Other
  const setDesiredCharge = useCallback((amount: number) => {
    dispatch({ type: 'SET_DESIRED_CHARGE', payload: amount });
  }, []);

  const selectCombo = useCallback((combo: Combo | null) => {
    dispatch({ type: 'SELECT_COMBO', payload: combo });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Computed - Validation
  const validation = useMemo(
    () => validateMultiQuoteQuotation(state.quotation),
    [state.quotation]
  );

  // Computed - Combo Ranking
  const ranking = useMemo<ComboRanking | null>(() => {
    if (!validation.isValid) {
      return null;
    }
    return generateComboRanking(state.quotation);
  }, [state.quotation, validation.isValid]);

  // Computed - Selected Combo (auto-select best if none selected)
  const selectedCombo = useMemo<Combo | null>(() => {
    if (state.selectedCombo) {
      return state.selectedCombo;
    }
    return ranking?.bestCombo ?? null;
  }, [state.selectedCombo, ranking]);

  // Computed - Profit Analysis (for selected combo)
  const profitAnalysis = useMemo<ProfitAnalysis | null>(() => {
    if (!selectedCombo || state.desiredChargeAmount <= 0) {
      return null;
    }

    // Create a temporary quotation from the selected combo for profit calculation
    const tempQuotation = {
      airlinePrice: state.quotation.airlinePrice,
      outbound: selectedCombo.outbound,
      return: selectedCombo.return,
      isRoundTrip: state.quotation.isRoundTrip,
    };

    return calculateProfitAnalysisFromDesiredProfit(tempQuotation, state.desiredChargeAmount);
  }, [selectedCombo, state.desiredChargeAmount, state.quotation.airlinePrice, state.quotation.isRoundTrip]);

  return {
    // State
    quotation: state.quotation,
    desiredChargeAmount: state.desiredChargeAmount,
    ranking,
    selectedCombo,
    profitAnalysis,
    validation,

    // Actions
    setAirlinePrice,
    setRoundTrip,
    addOutboundQuote,
    updateOutboundQuote,
    removeOutboundQuote,
    addReturnQuote,
    updateReturnQuote,
    removeReturnQuote,
    setDesiredCharge,
    selectCombo,
    reset,
  };
}
